<template>
  <div class="pa-4">

    <!-- BACK BUTTON -->
    <v-btn @click="$router.push('/dashboard')" class="back-btn mb-6" large>
      ← Back
    </v-btn>

    <h1 class="text-center mb-8" style="color: #333; font-weight: bold;">
      Manage Admin Users
    </h1>

    <!-- USERS TABLE -->
    <v-data-table
      :headers="headers"
      :items="users"
      :loading="loadingTable"
      class="elevation-3"
      style="border-radius: 16px; overflow: hidden;"
    >
      <template v-slot:item.roles="{ item }">
        <div v-if="item.roles?.length">
          <v-chip v-for="r in item.roles" :key="r" small color="#008080" dark class="ma-1 font-weight-bold">
            {{ r }}
          </v-chip>
        </div>
        <span v-else class="grey--text">No roles</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn small dark color="#008080" @click="editUser(item)" class="px-5">
          Edit
        </v-btn>
      </template>
    </v-data-table>

    <!-- FLOATING + BUTTON -->
    <v-btn @click="startCreating" fab large fixed bottom right class="save-btn" style="z-index: 10;">
      <v-icon large color="white">mdi-plus</v-icon>
    </v-btn>

    <!-- DIALOG -->
    <v-dialog v-model="showForm" persistent max-width="650">
      <v-card class="pa-10" style="border-radius: 20px;">
        <h2 class="text-center mb-8" style="color: #008080; font-weight: bold; font-size: 24px;">
          {{ editMode ? 'Edit User' : 'Create New User' }}
        </h2>

        <v-row>
          <v-col cols="12" md="6">
            <div class="header">Name</div>
            <v-text-field v-model="form.name" solo outlined dense :rules="nameRules" />
          </v-col>
          <v-col cols="12" md="6">
            <div class="header">Surname</div>
            <v-text-field v-model="form.surname" solo outlined dense :rules="nameRules" />
          </v-col>
        </v-row>

        <div class="header">Email Address</div>
        <v-text-field v-model="form.email" solo outlined dense :rules="emailRules" />

        <div v-if="!editMode" class="header">Password</div>
        <v-text-field v-if="!editMode" v-model="form.password" solo outlined dense type="password" :rules="passwordRules" />

        <div class="header">Contact No.</div>
        <v-text-field v-model="form.contact" solo outlined dense :rules="contactRules" />

        <div class="header">User Roles</div>
        <v-select
          v-model="form.roles"
          :items="availableRoles"
          multiple chips small-chips solo outlined dense
          color="#008080" item-color="#008080" class="mb-6"
        >
          <template v-slot:selection="{ item }">
            <v-chip small color="#008080" dark class="ma-1">{{ item }}</v-chip>
          </template>
        </v-select>

        <v-row justify="center" class="mt-8">
          <v-btn @click="showForm = false" class="back-btn mx-4" large style="width: 140px;">Cancel</v-btn>
          <v-btn @click="saveUser" :loading="saving" class="save-btn mx-4" large style="width: 180px; font-weight: bold;">
            {{ editMode ? 'Update' : 'Create' }} User
          </v-btn>
        </v-row>
      </v-card>
    </v-dialog>

    <v-overlay :value="saving">
      <v-progress-circular size="90" color="#008080" indeterminate />
    </v-overlay>
  </div>
</template>

<script>
import { auth } from "../firebaseConfig.js";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  setDoc,
  addDoc,
} from "firebase/firestore";
import { firebaseApp } from "../firebaseConfig";

const db = getFirestore(firebaseApp);

// LOG EVENT FUNCTION — NOW BUILT-IN (no import error!)
async function logEvent(action, target = "", type = "General") {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const event = {
    date: Math.floor(Date.now() / 1000),
    eventType: type,
    eventAction: action,
    user: currentUser.mobile || currentUser.email || "System",
    userName: `${currentUser.name || ""} ${currentUser.lastname || ""}`.trim() || "System",
    targetEmail: target,
  };
  try {
    await addDoc(collection(db, "events"), event);
  } catch (e) {
    console.error("Failed to log event:", e);
  }
}

export default {
  name: "UsersManagement",
  data() {
    return {
      loadingTable: true,
      saving: false,
      showForm: false,
      editMode: false,
      currentUserId: "",

      users: [],

      form: {
        name: "", surname: "", email: "", password: "", contact: "", roles: ["admin"]
      },

      availableRoles: ["admin", "super admin"],

      headers: [
        { text: "Name", value: "name" },
        { text: "Surname", value: "lastname" },
        { text: "Email", value: "email" },
        { text: "Mobile", value: "mobile" },
        { text: "Roles", value: "roles", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],

      nameRules: [v => !!v || "Required", v => /^[a-zA-Z ]+$/.test(v) || "Letters only"],
      emailRules: [v => !!v || "Required", v => /.+@.+\..+/.test(v) || "Valid email"],
      passwordRules: [
        v => !!v || "Required",
        v => v.length >= 6 || "Min 6 chars",
        v => /(?=.*[A-Z])/.test(v) || "Uppercase needed",
        v => /(?=.*\d)/.test(v) || "Number needed",
        v => /(?=.*[!@#$%^&*])/.test(v) || "Special char needed",
      ],
      contactRules: [v => !!v || "Required", v => /^\d{10}$/.test(v) || "10 digits only"],
    };
  },

  async created() {
    await this.loadUsers();
  },

  methods: {
    async loadUsers() {
      this.loadingTable = true;
      try {
        const snap = await getDocs(collection(db, "adminUsers"));
        this.users = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (e) {
        console.error(e);
      }
      this.loadingTable = false;
    },

    startCreating() {
      this.editMode = false;
      this.currentUserId = "";
      this.form = { name: "", surname: "", email: "", password: "", contact: "", roles: ["admin"] };
      this.showForm = true;
    },

    editUser(user) {
      this.editMode = true;
      this.currentUserId = user.id;
      this.form = {
        name: user.name || "",
        surname: user.lastname || "",
        email: user.email || "",
        password: "",
        contact: user.mobile || "",
        roles: Array.isArray(user.roles) ? user.roles : ["admin"],
      };
      this.showForm = true;
    },

    async saveUser() {
      // Validation
      if (!this.form.name || !this.form.surname || !this.form.email ||
          (!this.editMode && (!this.form.password || !this.form.contact)) ||
          this.form.roles.length === 0) {
        alert("Please fill all required fields and select at least one role");
        return;
      }

      this.saving = true;

      try {
        let uid = this.currentUserId;

        // Create Firebase Auth user (only when creating new)
        if (!this.editMode) {
          const cred = await createUserWithEmailAndPassword(auth, this.form.email, this.form.password);
          uid = cred.user.uid;
        }

        // Save to Firestore
        await setDoc(doc(db, "adminUsers", uid), {
          name: this.form.name,
          lastname: this.form.surname,
          email: this.form.email,
          mobile: this.form.contact,
          roles: this.form.roles,
          AccountDisabled: false,
          updatedAt: new Date(),
        }, { merge: true });

        // Log to events + audit trail
        const action = this.editMode ? "User Updated" : "User Created";
        await addDoc(collection(db, "events"), {
          date: Math.floor(Date.now() / 1000),
          eventType: "User Management",
          eventAction: action,
          user: JSON.parse(localStorage.getItem("currentUser") || "{}").mobile || "system",
          targetEmail: this.form.email,
        });

        // Also log with our safe function
        await logEvent(action, this.form.email, "User Management");

        alert(`User ${this.editMode ? "updated" : "created"} successfully!`);
        this.showForm = false;
        await this.loadUsers();
      } catch (error) {
        console.error("Save failed:", error);
        const msg = error.code === "auth/email-already-in-use"
          ? "This email is already in use"
          : error.code === "auth/weak-password"
            ? "Password is too weak"
            : "Failed to save user. Check internet or permissions.";
        alert(msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.save-btn {
  background: linear-gradient(0deg, rgba(241, 194, 132, 1) 0%, rgba(231, 184, 122, 1) 100%) !important;
  color: white !important;
  border-radius: 30px !important;
  font-weight: bold !important;
  box-shadow: 0 4px 15px rgba(241, 194, 132, 0.4) !important;
  text-transform: none !important;
}

.back-btn {
  background-color: #666 !important;
  color: white !important;
  border-radius: 30px !important;
  text-transform: none !important;
}

.header {
  font-weight: 600;
  margin: 16px 0 8px 4px;
  color: #333;
}

.v-data-table { background: white !important; }
</style>